// client/src/pages/CourseGenAI.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, CheckCircle, ChevronDown, ChevronRight, 
  FileText, Award, Cpu, Calculator, Lock, 
  ShieldCheck, Zap, Database, BarChart3, Info,
  Search, RefreshCw, AlertTriangle, Eye, Layers
} from "lucide-react";

// --- INTERACTIVE TOOL: VRAM CALCULATOR ---
const VRAMCalculator = () => {
  const [params, setParams] = useState(70);
  const [precision, setPrecision] = useState(0.5); // 4-bit
  const [context, setContext] = useState(8192);
  
  const modelVRAM = params * precision;
  const overhead = modelVRAM * 0.2; // 20% buffer for KV Cache/Activations
  const total = (modelVRAM + overhead).toFixed(2);

  return (
    <div className="bg-slate-900 text-white p-6 md:p-10 rounded-[2.5rem] my-10 border border-orange-500/30 shadow-2xl relative overflow-hidden">
      <div className="absolute top-0 right-0 p-8 opacity-10"><Cpu size={120} /></div>
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-orange-500 rounded-2xl"><Calculator size={24} className="text-white" /></div>
          <div>
            <h4 className="font-black uppercase tracking-tighter text-xl text-white">Hardware Sizer</h4>
            <p className="text-orange-400 text-[10px] font-bold uppercase tracking-widest">Architectural Inference Tool</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Model Parameters (Billions)</label>
            <input type="number" value={params} onChange={(e) => setParams(Number(e.target.value))} className="w-full bg-slate-800 border border-slate-700 rounded-2xl p-4 text-white focus:ring-2 focus:ring-orange-500 outline-none transition-all" />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Quantization (Precision)</label>
            <select value={precision} onChange={(e) => setPrecision(Number(e.target.value))} className="w-full bg-slate-800 border border-slate-700 rounded-2xl p-4 text-white focus:ring-2 focus:ring-orange-500 outline-none transition-all">
              <option value={2}>FP16 / BF16 (2 Bytes)</option>
              <option value={1}>INT8 (1 Byte)</option>
              <option value={0.5}>INT4 / QLoRA (0.5 Bytes)</option>
            </select>
          </div>
          <div className="bg-gradient-to-br from-orange-600 to-orange-400 rounded-[2rem] p-6 flex flex-col justify-center items-center shadow-xl shadow-orange-500/20">
            <span className="text-4xl font-black text-white">{total} GB</span>
            <span className="text-[10px] uppercase font-bold text-orange-100 mt-1">Total Recommended VRAM</span>
          </div>
        </div>
        <div className="flex items-start gap-3 p-4 bg-orange-500/10 rounded-2xl border border-orange-500/20">
          <Info size={16} className="text-orange-500 mt-1 shrink-0" />
          <p className="text-xs text-slate-300 leading-relaxed">
            <strong>Formula:</strong> $VRAM \approx (Parameters \times Precision) \times 1.2$. The 20% buffer accounts for the <strong>KV Cache</strong> and intermediate activations during inference.
          </p>
        </div>
      </div>
    </div>
  );
};

// --- INTERACTIVE TOOL: TOKEN COST ESTIMATOR ---
const TokenCostEstimator = () => {
    const [tokens, setTokens] = useState(1000000);
    const [inputPrice, setInputPrice] = useState(0.15); // per million
    const [outputPrice, setOutputPrice] = useState(0.60);

    const cost = (tokens / 1000000 * (inputPrice + outputPrice)).toFixed(2);

    return (
        <div className="bg-white border-2 border-slate-100 p-8 rounded-[2.5rem] my-10 shadow-xl">
            <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-slate-900 rounded-2xl"><BarChart3 size={24} className="text-white" /></div>
                <h4 className="font-black text-slate-900 text-xl tracking-tight">Token-Based ROI Calculator</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div className="space-y-4">
                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Monthly Tokens (Est.)</label>
                        <input type="range" min="100000" max="10000000" step="100000" value={tokens} onChange={(e) => setTokens(Number(e.target.value))} className="w-full accent-orange-500" />
                        <div className="flex justify-between text-[10px] font-bold text-slate-400 mt-1"><span>100K</span><span>10M</span></div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-2xl flex justify-between items-center">
                        <span className="text-sm font-bold text-slate-600">Total Tokens:</span>
                        <span className="text-sm font-black text-slate-900">{(tokens/1000).toLocaleString()}K</span>
                    </div>
                </div>
                <div className="bg-slate-900 rounded-[2rem] p-8 text-center">
                    <p className="text-orange-400 text-[10px] font-black uppercase tracking-widest mb-2">Estimated OpEx</p>
                    <h3 className="text-5xl font-black text-white">${cost}<span className="text-lg text-slate-500">/mo</span></h3>
                </div>
            </div>
        </div>
    );
};

// --- QUIZ COMPONENT ---
const QuizEngine = ({ questionData, onComplete }) => {
  const [selected, setSelected] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);

  const handleCheck = () => {
    if (selected === questionData.correct) {
      setIsCorrect(true);
    } else {
      setIsCorrect(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-10 rounded-[3rem] shadow-2xl border border-slate-100 my-10 transition-all">
      <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
        <Award className="text-orange-600" size={32} />
      </div>
      <h3 className="text-2xl font-black text-slate-900 mb-6 leading-tight">{questionData.q}</h3>
      <div className="space-y-3 mb-8">
        {questionData.options.map((opt, idx) => (
          <button 
            key={idx} 
            onClick={() => {setSelected(idx); setIsCorrect(null);}}
            className={`w-full text-left p-5 rounded-2xl font-bold transition-all border-2 
              ${selected === idx ? 'border-orange-500 bg-orange-50 text-orange-900' : 'border-slate-50 bg-slate-50 text-slate-600 hover:border-slate-200'}`}
          >
            {opt}
          </button>
        ))}
      </div>
      
      {isCorrect === true && (
        <div className="p-4 bg-green-100 text-green-700 rounded-2xl font-bold mb-6 flex items-center gap-2">
            <CheckCircle size={20}/> Verification Successful. Proceed to next submodule.
        </div>
      )}
      {isCorrect === false && (
        <div className="p-4 bg-red-100 text-red-700 rounded-2xl font-bold mb-6 flex items-center gap-2">
            <AlertTriangle size={20}/> Strategy Mismatch. Review architectural foundations.
        </div>
      )}

      <button 
        disabled={selected === null}
        onClick={handleCheck}
        className="w-full py-5 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-orange-600 disabled:opacity-50 transition-all"
      >
        Validate Response
      </button>
    </div>
  );
};

// --- CORE COURSE DATA ---
// --- CORE COURSE DATA (UPDATED: 6 MODULES, FULL GENAI ARCHITECT CONTENT) ---
const COURSE_CONTENT = {
  title: "Generative AI Architect Certification",
  modules: [
    {
      id: 1,
      title: "LLM & Transformer Foundations",
      lessons: [
        {
          id: 101,
          title: "How Large Language Models Work",
          type: "text",
          content: `
            <p class="text-lg text-slate-600 mb-6">
              Large Language Models (LLMs) are probabilistic systems trained to predict the next token in a sequence.
              Their intelligence emerges from scale, data, and the Transformer architecture.
            </p>
            <p class="text-slate-600 mb-6">
              The Transformer replaces recurrence with <strong>self-attention</strong>, allowing every token to
              attend to every other token in parallel. This enables long-range dependency modeling and massive scalability.
            </p>
            <div class="bg-slate-900 p-8 rounded-3xl text-orange-400 font-mono text-center mb-6">
              Attention(Q, K, V) = softmax(QKᵀ / √dₖ)V
            </div>
            <p class="text-slate-600">
              Architects must understand that attention scales as <strong>O(n²)</strong>.
              Increasing context windows increases cost, latency, and VRAM usage non-linearly.
            </p>
          `
        },
        {
          id: 102,
          title: "Module 1 Assessment",
          type: "quiz",
          question: {
            q: "Why is self-attention critical in transformer-based LLMs?",
            options: [
              "It compresses text",
              "It enables parallel processing and long-range context",
              "It removes tokenization",
              "It reduces parameter count"
            ],
            correct: 1
          }
        }
      ]
    },

    {
      id: 2,
      title: "Cost, Performance & Model Routing",
      lessons: [
        {
          id: 201,
          title: "Token Economics & Compute Cost",
          type: "text",
          content: `
            <p class="text-lg text-slate-600 mb-6">
              In production GenAI systems, <strong>tokens are currency</strong>.
              Cost is driven by input tokens, output tokens, model size, and inference efficiency.
            </p>
            <p class="text-slate-600 mb-6">
              The dominant factor in GPU memory consumption is <strong>model parameter size</strong>,
              not temperature or prompt format.
            </p>
            <p class="text-slate-600">
              Architects control cost by limiting context, compressing prompts,
              routing queries intelligently, and using smaller or distilled models where possible.
            </p>
          `
        },
        {
          id: 202,
          title: "Hardware Sizing Tool",
          type: "interactive-tool",
          toolType: "vram"
        },
        {
          id: 203,
          title: "Module 2 Assessment",
          type: "quiz",
          question: {
            q: "Which strategy most effectively reduces GenAI operating cost at scale?",
            options: [
              "Using only the largest model",
              "Increasing temperature",
              "Model routing based on task complexity",
              "Increasing context window"
            ],
            correct: 2
          }
        }
      ]
    },

    {
      id: 3,
      title: "Embeddings & Vector Search",
      lessons: [
        {
          id: 301,
          title: "Semantic Embeddings & Similarity",
          type: "text",
          content: `
            <p class="text-lg text-slate-600 mb-6">
              Embeddings convert text into high-dimensional vectors such that semantic similarity
              is represented by geometric proximity.
            </p>
            <p class="text-slate-600 mb-6">
              Vector databases use Approximate Nearest Neighbor (ANN) algorithms such as
              <strong>HNSW</strong> to make large-scale semantic search computationally feasible.
            </p>
            <p class="text-slate-600">
              High-dimensional embeddings improve recall but increase storage cost and retrieval latency.
              Architects must balance accuracy and system performance.
            </p>
          `
        },
        {
          id: 302,
          title: "Module 3 Assessment",
          type: "quiz",
          question: {
            q: "Why are ANN algorithms like HNSW used in vector databases?",
            options: [
              "They guarantee perfect accuracy",
              "They reduce embedding size",
              "They enable fast similarity search at scale",
              "They eliminate hallucinations"
            ],
            correct: 2
          }
        }
      ]
    },

    {
      id: 4,
      title: "Retrieval-Augmented Generation (RAG)",
      lessons: [
        {
          id: 401,
          title: "RAG Architecture & Chunking",
          type: "text",
          content: `
            <p class="text-lg text-slate-600 mb-6">
              Retrieval-Augmented Generation (RAG) grounds LLM responses in external knowledge
              by retrieving relevant document chunks at inference time.
            </p>
            <p class="text-slate-600 mb-6">
              Chunking strategy is critical. Chunks that are too large dilute meaning,
              while chunks that are too small lose context.
            </p>
            <p class="text-slate-600">
              RAG is preferred over fine-tuning when data is large, private, or frequently changing.
            </p>
          `
        },
        {
          id: 402,
          title: "Module 4 Assessment",
          type: "quiz",
          question: {
            q: "Why does overly large chunk size reduce RAG answer quality?",
            options: [
              "It increases tokenization errors",
              "It injects irrelevant semantic noise",
              "It prevents embeddings",
              "It lowers GPU utilization"
            ],
            correct: 1
          }
        }
      ]
    },

    {
      id: 5,
      title: "Fine-Tuning, LoRA & Drift",
      lessons: [
        {
          id: 501,
          title: "Instruction Tuning & LoRA",
          type: "text",
          content: `
            <p class="text-lg text-slate-600 mb-6">
              Fine-tuning adapts <strong>model behavior</strong>, not factual knowledge.
              Instruction tuning improves consistency, format control, and task alignment.
            </p>
            <p class="text-slate-600 mb-6">
              LoRA (Low-Rank Adaptation) enables parameter-efficient fine-tuning by updating
              a very small number of weights, drastically reducing training cost.
            </p>
            <p class="text-slate-600">
              Fine-tuning must be monitored carefully to avoid data drift and silent degradation.
            </p>
          `
        },
        {
          id: 502,
          title: "Module 5 Assessment",
          type: "quiz",
          question: {
            q: "What is the main benefit of LoRA?",
            options: [
              "Larger context windows",
              "Lower inference cost",
              "Minimal trainable parameters",
              "Eliminates RAG"
            ],
            correct: 2
          }
        }
      ]
    },

    {
      id: 6,
      title: "Inference, Safety & Governance",
      lessons: [
        {
          id: 601,
          title: "Scaling, Safety & Compliance",
          type: "text",
          content: `
            <p class="text-lg text-slate-600 mb-6">
              Production GenAI systems require batching, distributed inference,
              safety layers, and governance controls.
            </p>
            <p class="text-slate-600 mb-6">
              Safety must be enforced <strong>outside the model</strong> using moderation layers,
              rule-based validators, and human-in-the-loop review for high-risk decisions.
            </p>
            <p class="text-slate-600">
              Enterprises must ensure privacy, auditability, and compliance
              through controlled deployment and monitoring pipelines.
            </p>
          `
        },
        {
          id: 602,
          title: "Final Assessment",
          type: "quiz",
          question: {
            q: "Why is human-in-the-loop required in enterprise GenAI systems?",
            options: [
              "To increase latency",
              "To reduce GPU cost",
              "To provide oversight for critical decisions",
              "To replace evaluation metrics"
            ],
            correct: 2
          }
        }
      ]
    }
  ]
};


function CourseGenAI() {
  const navigate = useNavigate();
  const [activeModuleId, setActiveModuleId] = useState(COURSE_CONTENT.modules[0].id);
  const [activeLesson, setActiveLesson] = useState(COURSE_CONTENT.modules[0].lessons[0]);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Auto-scroll to top when lesson changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeLesson]);

  const renderContent = () => {
    switch(activeLesson.type) {
        case 'quiz':
            return <QuizEngine questionData={activeLesson.question} />;
        case 'interactive-tool':
            return (
                <div className="space-y-6">
                    <p class="text-lg text-slate-600">Calculators are essential for estimating infrastructure costs and hardware requirements before deploying at scale.</p>
                    {activeLesson.toolType === 'vram' ? <VRAMCalculator /> : <TokenCostEstimator />}
                </div>
            );
        default:
            return <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: activeLesson.content }} />;
    }
  };

  return (
    <div className="h-screen w-screen flex flex-col bg-white overflow-hidden font-sans">
      
      {/* Cinematic Header */}
      <header className="h-20 bg-white border-b border-slate-100 flex items-center justify-between px-8 shrink-0 z-30">
        <div className="flex items-center gap-6">
          <button onClick={() => navigate('/dashboard')} className="p-3 hover:bg-slate-50 rounded-xl border border-slate-100 transition-all text-slate-400 hover:text-orange-600">
            <ArrowLeft size={20} />
          </button>
          <div className="h-8 w-[1px] bg-slate-100"></div>
          <div>
            <h1 className="text-xl font-black text-slate-900 tracking-tighter uppercase">{COURSE_CONTENT.title}</h1>
            <p className="text-[10px] font-black text-orange-500 uppercase tracking-widest">Orivon Academy • Professional Track</p>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-4">
            <div className="h-2 w-48 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 rounded-full" style={{width: `${(activeLesson.id / 902) * 100}%`}}></div>
            </div>
            <span className="text-[10px] font-black text-slate-400 uppercase">Progress</span>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        
        {/* Massive Navigation Sidebar */}
        <aside className={`${sidebarOpen ? 'w-80' : 'w-0'} bg-slate-50 border-r border-slate-100 flex flex-col transition-all duration-300 overflow-hidden shrink-0`}>
           <div className="p-6 overflow-y-auto space-y-4">
              {COURSE_CONTENT.modules.map((module) => (
                 <div key={module.id} className="space-y-1">
                    <button 
                       onClick={() => setActiveModuleId(activeModuleId === module.id ? null : module.id)}
                       className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all text-left
                         ${activeModuleId === module.id ? 'bg-white shadow-md border border-slate-100' : 'hover:bg-slate-200/50'}`}
                    >
                       <span className="text-sm font-black text-slate-800">{module.title}</span>
                       {activeModuleId === module.id ? <ChevronDown size={16} className="text-orange-500"/> : <ChevronRight size={16} className="text-slate-300"/>}
                    </button>

                    {activeModuleId === module.id && (
                       <div className="ml-2 pl-4 border-l-2 border-slate-200 space-y-1 mt-2">
                          {module.lessons.map((lesson) => (
                             <button 
                                key={lesson.id} 
                                onClick={() => setActiveLesson(lesson)}
                                className={`w-full flex items-center gap-3 p-3 text-left text-xs font-bold transition-all rounded-xl
                                   ${activeLesson.id === lesson.id 
                                      ? 'bg-orange-500 text-white shadow-lg' 
                                      : 'text-slate-500 hover:bg-slate-100'}`}
                             >
                                {lesson.type === 'quiz' ? <Award size={14}/> : lesson.type === 'interactive-tool' ? <Cpu size={14}/> : <FileText size={14}/>}
                                <span className="truncate">{lesson.title}</span>
                             </button>
                          ))}
                       </div>
                    )}
                 </div>
              ))}
           </div>
        </aside>

        {/* Content Portal */}
        <main className="flex-1 overflow-y-auto bg-white p-6 md:p-16">
           <div className="max-w-4xl mx-auto">
              {/* Top Meta */}
              <div className="mb-12">
                 <div className="flex items-center gap-2 mb-4">
                    <span className="bg-orange-100 text-orange-600 text-[10px] font-black px-3 py-1 rounded-full uppercase">Module 0{activeLesson.id.toString().charAt(0)}</span>
                    <span className="text-slate-300 font-bold text-[10px] uppercase tracking-widest">Engineering Documentation</span>
                 </div>
                 <h2 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tighter leading-tight mb-4">
                    {activeLesson.title}
                 </h2>
                 <p className="text-slate-400 font-medium">Deep-dive into professional LLM architecture and system design.</p>
              </div>

              {/* Dynamic Content */}
              <div className="min-h-[400px]">
                {renderContent()}
              </div>

              {/* Navigation Footer */}
              <div className="mt-20 pt-10 border-t border-slate-100 flex justify-between items-center">
                 <button className="text-slate-400 hover:text-slate-900 font-black uppercase text-xs tracking-widest transition-colors flex items-center gap-2">
                    <ArrowLeft size={16}/> Previous Lesson
                 </button>
                 <button 
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-4 bg-slate-50 text-slate-400 hover:text-slate-900 rounded-full transition-all"
                 >
                    <RefreshCw size={20}/>
                 </button>
                 <button className="group bg-slate-900 text-white px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-orange-600 transition-all flex items-center gap-3 shadow-xl">
                    Next Chapter
                    <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform"/>
                 </button>
              </div>
              
              <footer className="mt-20 text-center">
                  <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.4em]">End of Module Section • Orivon Academy © 2025</p>
              </footer>
           </div>
        </main>

      </div>
    </div>
  );
}

export default CourseGenAI;